import { FC } from "react";

import { t } from "i18n/i18n";
import { BaseTextArea } from "mds/components/TextArea";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentUser } from "store/selectors";
import { toastSuccessMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { useLocalCopy } from "utils/collections";
import { rollbarAndLogError } from "utils/logger";
import { isAssessmentAIFeedbackStyleEnabled } from "utils/waffle-flags";

const MAX_ASSESSMENT_PROMPT_LEN = 50_000;

/** Account Settings AI tab — edits optional `assessment_ai_feedback_style` (waffle-gated). */
export const AISettingsTab: FC<{ onClose: () => void; readOnly?: boolean }> = ({
  onClose,
  readOnly = false,
}) => {
  const user = useAppSelector(selectCurrentUser);
  const storedValue = user?.assessment_ai_feedback_style ?? "";
  const [draft, setDraft] = useLocalCopy(storedValue);

  const handleSave = async () => {
    if (readOnly || !user) {
      onClose();
      return;
    }
    const next = draft.trim() || null;
    const currentStored = user?.assessment_ai_feedback_style
      ? user.assessment_ai_feedback_style.trim()
      : null;
    if (next === currentStored) {
      onClose();
      return;
    }
    trackEvent("Account Settings - Save AI changes", BUTTON_EVENT_CATEGORY);
    try {
      await storeApi.users.partial_update({
        id: user.id,
        assessment_ai_feedback_style: next,
      });
      toastSuccessMessage(t("success.toasts.updated_account_settings"));
      onClose();
    } catch (error: unknown) {
      rollbarAndLogError("assessment_ai_feedback_style partial_update", error);
    }
  };

  if (!isAssessmentAIFeedbackStyleEnabled()) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 text-sm font-medium leading-[1.3125rem] text-black-tint-20">
      <label className="flex flex-col gap-1">
        <h3>{t("home.settings.assessment_ai_feedback_style.title")}</h3>
        <p className="body-xs mb-2 text-black-tint-55">
          {t("home.settings.assessment_ai_feedback_style.description")}
        </p>
        <BaseTextArea
          className="w-full"
          disabled={readOnly}
          maxLength={MAX_ASSESSMENT_PROMPT_LEN}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
      </label>

      <div className="flex justify-end gap-2 pt-8">
        <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </SquareButton>

        <SquareButton kind="accent" size="m" title={t("common.save_changes")} onClick={handleSave}>
          {t("common.save_changes")}
        </SquareButton>
      </div>
    </div>
  );
};
